import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowUpRight, TrendingUp, Users, PlayCircle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';

const CaseStudies = () => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];

  const caseStudies = [
    {
      id: 1,
      company: "TechStart Zürich",
      industry: "Tech Startup",
      service: "AI-Video + SEO",
      before: {
        traffic: "2,400",
        leads: "8",
        conversion: "1.2%"
      },
      after: {
        traffic: "12,800",
        leads: "45",
        conversion: "3.8%"
      },
      duration: "6 Monate",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      videoUrl: "#",
      description: {
        de: "Komplette Neupositionierung mit AI-Erklärvideos und lokalem SEO für den Zürcher Markt.",
        en: "Complete repositioning with AI explainer videos and local SEO for the Zurich market.",
        fr: "Repositionnement complet avec des vidéos explicatives IA et SEO local pour le marché zurichois.",
        it: "Riposizionamento completo con video esplicativi IA e SEO locale per il mercato di Zurigo."
      }
    },
    {
      id: 2,
      company: "MedPraxis Basel",
      industry: "Gesundheitswesen",
      service: "AI-Video Patientenaufklärung",
      before: {
        traffic: "890",
        leads: "12",
        conversion: "2.1%"
      },
      after: {
        traffic: "3,200",
        leads: "38",
        conversion: "4.6%"
      },
      duration: "4 Monate",
      image: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      videoUrl: "#",
      description: {
        de: "Patientenvertrauen durch personalisierte AI-Aufklärungsvideos und mehrsprachige Inhalte gestärkt.",
        en: "Patient trust strengthened through personalized AI education videos and multilingual content.",
        fr: "Confiance des patients renforcée grâce à des vidéos éducatives IA personnalisées et contenu multilingue.",
        it: "Fiducia dei pazienti rafforzata attraverso video educativi IA personalizzati e contenuti multilingue."
      }
    },
    {
      id: 3,
      company: "E-Shop Luxury Genève",
      industry: "E-Commerce",
      service: "Produktvideos + Webdesign",
      before: {
        traffic: "5,600",
        leads: "28",
        conversion: "0.8%"
      },
      after: {
        traffic: "18,900",
        leads: "156",
        conversion: "2.9%"
      },
      duration: "8 Monate",
      image: "https://images.unsplash.com/photo-1516321497487-e288fb19713f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      videoUrl: "#",
      description: {
        de: "Luxury E-Commerce mit AI-Produktvideos und Premium-Webdesign für französischsprachige Kundschaft optimiert.",
        en: "Luxury e-commerce optimized with AI product videos and premium web design for French-speaking clientele.",
        fr: "E-commerce de luxe optimisé avec des vidéos produits IA et design web premium pour la clientèle francophone.",
        it: "E-commerce di lusso ottimizzato con video prodotti IA e design web premium per clientela francofona."
      }
    }
  ];

  const getGrowthPercentage = (before: string, after: string) => {
    const beforeNum = parseInt(before.replace(/[,\.]/g, ''));
    const afterNum = parseInt(after.replace(/[,\.]/g, ''));
    return Math.round(((afterNum - beforeNum) / beforeNum) * 100);
  };

  return (
    <section className="py-24 bg-gradient-to-b from-background to-background/95 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-32 h-32 bg-primary/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-accent/10 rounded-full blur-xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-16" data-aos="fade-up">
          <Badge variant="outline" className="mb-4 text-sm font-medium border-primary/20">
            Erfolgsgeschichten
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
            Bewiesene Resultate für Schweizer KMU
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Echte Projekte, messbare Erfolge. So helfen wir Schweizer Unternehmen beim Wachstum mit AI-Videos und digitalem Marketing.
          </p>
        </div>

        {/* Case Studies Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {caseStudies.map((study, index) => (
            <Card 
              key={study.id} 
              className="p-6 bg-gradient-to-br from-card to-card/80 border border-border/50 hover:border-primary/50 transition-all duration-500 group hover:shadow-xl hover:shadow-primary/10"
              data-aos="fade-up"
              data-aos-delay={index * 100}
            >
              {/* Company Header */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-1">{study.company}</h3>
                  <Badge variant="secondary" className="text-xs">
                    {study.industry}
                  </Badge>
                </div>
                <Button
                  variant="ghost" 
                  size="sm"
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-2 hover:bg-primary/10"
                >
                  <PlayCircle size={20} className="text-primary" />
                </Button>
              </div>

              {/* Service */}
              <div className="mb-4">
                <span className="text-sm text-muted-foreground font-medium">Service:</span>
                <p className="text-primary font-semibold">{study.service}</p>
              </div>

              {/* Image */}
              <div className="relative mb-6 rounded-lg overflow-hidden group-hover:scale-[1.02] transition-transform duration-500">
                <img 
                  src={study.image} 
                  alt={study.company}
                  className="w-full h-40 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent"></div>
              </div>

              {/* Metrics */}
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center p-3 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-lg border border-green-500/20">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-1">Traffic (Monat)</p>
                    <p className="text-lg font-bold text-foreground">{study.after.traffic}</p>
                    <p className="text-xs text-green-500 flex items-center justify-center">
                      <TrendingUp size={12} className="mr-1" />
                      +{getGrowthPercentage(study.before.traffic, study.after.traffic)}%
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-1">Leads</p>
                    <p className="text-lg font-bold text-foreground">{study.after.leads}</p>
                    <p className="text-xs text-green-500 flex items-center justify-center">
                      <Users size={12} className="mr-1" />
                      +{getGrowthPercentage(study.before.leads, study.after.leads)}%
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-1">Conversion</p>
                    <p className="text-lg font-bold text-foreground">{study.after.conversion}</p>
                    <p className="text-xs text-green-500 flex items-center justify-center">
                      <ArrowUpRight size={12} className="mr-1" />
                      +{getGrowthPercentage(study.before.conversion.replace('%', ''), study.after.conversion.replace('%', ''))}%
                    </p>
                  </div>
                </div>
              </div>

              {/* Description */}
              <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                {study.description[currentLanguage as keyof typeof study.description]}
              </p>

              {/* Duration */}
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">
                  Dauer: {study.duration}
                </span>
                <Button variant="ghost" size="sm" className="text-primary hover:text-primary/80 p-0">
                  Case Study →
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center" data-aos="fade-up" data-aos-delay="300">
          <Button size="lg" className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-primary-foreground font-semibold px-8 py-3">
            Ihr Erfolg mit uns planen
            <ArrowUpRight className="ml-2" size={20} />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;